import { useAuth } from '@/contexts/AuthContext';
import { useCallback, useRef } from 'react';

interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
  skipRetry?: boolean; // Skip automatic retry on 401
}

/**
 * Custom hook that provides an authenticated fetch function
 * Automatically includes the Azure AD Bearer token in requests
 * Automatically retries with a fresh token on 401 responses
 *
 * IMPORTANT: Only call authFetch when isReady is true from useAuth()
 * This ensures MSAL has finished initializing and the token is available
 */
export function useAuthFetch() {
  const { getAccessToken, isAuthenticated, login } = useAuth();
  const isRefreshingRef = useRef(false);

  const authFetch = useCallback(
    async (url: string, options: FetchOptions = {}): Promise<Response> => {
      const { skipAuth, skipRetry, ...fetchOptions } = options;

      // If skipAuth is true, make a regular fetch
      if (skipAuth) {
        return fetch(url, fetchOptions);
      }

      // If not authenticated, make unauthenticated request
      if (!isAuthenticated) {
        console.warn('[authFetch] Not authenticated, making unauthenticated request');
        return fetch(url, fetchOptions);
      }

      // Get the access token
      let token = await getAccessToken();

      if (!token) {
        console.error('[authFetch] Failed to get access token!');
        throw new Error(
          'Failed to acquire authentication token. Please ensure you are logged in and try again.'
        );
      }

      // Add the Authorization header
      const headers = new Headers(fetchOptions.headers);
      headers.set('Authorization', `Bearer ${token}`);

      const response = await fetch(url, {
        ...fetchOptions,
        headers,
      });

      // Handle 401 Unauthorized - token might be expired
      if (response.status === 401 && !skipRetry && !isRefreshingRef.current) {
        console.warn('[authFetch] Received 401, attempting token refresh with forceRefresh=true...');
        isRefreshingRef.current = true;

        try {
          // Force refresh the token - pass true to force MSAL to get a new token
          token = await getAccessToken(true);

          if (token) {
            // Retry the request with the new token
            const retryHeaders = new Headers(fetchOptions.headers);
            retryHeaders.set('Authorization', `Bearer ${token}`);

            const retryResponse = await fetch(url, {
              ...fetchOptions,
              headers: retryHeaders,
            });

            isRefreshingRef.current = false;
            return retryResponse;
          } else {
            // Token refresh failed, user needs to re-authenticate
            console.error('[authFetch] Token refresh failed, redirecting to login...');
            isRefreshingRef.current = false;
            
            // Trigger login flow
            await login();
            throw new Error('Session expired. Please log in again.');
          }
        } catch (refreshError) {
          isRefreshingRef.current = false;
          console.error('[authFetch] Token refresh error:', refreshError);
          throw refreshError;
        }
      }

      return response;
    },
    [getAccessToken, isAuthenticated, login]
  );

  return { authFetch };
}

/**
 * Helper function to create authenticated fetch for use outside of components
 * Note: This requires the token to be passed in
 */
export async function fetchWithToken(
  url: string,
  token: string,
  options: RequestInit = {}
): Promise<Response> {
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${token}`);

  return fetch(url, {
    ...options,
    headers,
  });
}
